import { uniqueId } from 'lodash-es';
import {
    FocusEvent,
    HTMLAttributes,
    KeyboardEvent,
    MouseEvent,
    RefObject,
    useCallback,
    useMemo,
    useRef,
    useState
} from 'react';
import { isIOS, isMacOS } from '../../../utils/browser.utils';

type ActionAreaEvents<T> = Pick<
    HTMLAttributes<T>,
    | 'onClick'
    | 'onMouseEnter'
    | 'onMouseLeave'
    | 'onFocus'
    | 'onBlur'
    | 'onMouseDown'
    | 'onKeyDown'
>;

type UseActionAreaReturnType<HTMLButtonElement> = {
    containerProps?: {
        'data-hover'?: true | null;
        'data-focus-visible'?: true | null;
        'data-interactive'?: true | null;
        'data-disabled'?: true | null;
        id?: string;
    };
    actionAreaProps?: {
        ref?: RefObject<HTMLButtonElement>;
        disabled?: boolean;
    } & HTMLAttributes<HTMLButtonElement> &
        ActionAreaEvents<HTMLButtonElement>;
};

export type UseActionAreaProps<T extends HTMLElement = HTMLButtonElement> = {
    enabled?: boolean;
    disabled?: boolean;
    id?: string;
} & ActionAreaEvents<T>;

const isInsideElementRect = <T extends HTMLElement = HTMLButtonElement>(
    elementRect: DOMRect,
    event: MouseEvent<T>
) =>
    event.clientX >= elementRect.left &&
    event.clientX <= elementRect.right &&
    event.clientY >= elementRect.top &&
    event.clientY <= elementRect.bottom;

/**
 * useActionArea is a hook for managing hover, focus, and keyboard interactions on interactive elements.
 *
 * Features:
 * - Handles hover, focus-visible, and keyboard states.
 * - Supports `Space` and `Enter` for triggering `onClick`.
 * - Includes refs and ARIA attributes for accessibility.
 */
export const useActionArea = <T extends HTMLElement = HTMLButtonElement>(
    props?: UseActionAreaProps<T>
): UseActionAreaReturnType<T> => {
    const {
        enabled = true,
        id,
        onClick,
        onMouseEnter,
        onMouseLeave,
        onFocus,
        onBlur,
        onMouseDown,
        onKeyDown,
        disabled
    } = props || {};

    const actionAreaRef = useRef<T>(null);
    const [isHovered, setIsHovered] = useState(false);
    const [isFocusVisible, setIsFocusVisible] = useState(false);
    const [isKeyboardFocusDetection, setIsKeyboardFocusDetection] =
        useState(false);

    const handleMouseEnter = useCallback(
        (event: MouseEvent<T>) => {
            if (disabled) {
                return;
            }
            setIsHovered(true);
            onMouseEnter?.(event);
        },
        [disabled, onMouseEnter]
    );

    const handleMouseLeave = useCallback(
        (event: MouseEvent<T>) => {
            if (disabled || !actionAreaRef.current) {
                return;
            }

            const elementRect = actionAreaRef.current.getBoundingClientRect();

            if (!isInsideElementRect(elementRect, event)) {
                setIsHovered(false);
                onMouseLeave?.(event);
            }
        },
        [disabled, onMouseLeave]
    );

    const handleMouseDown = useCallback(
        (event: MouseEvent<T>) => {
            if (disabled) {
                return;
            }
            setIsKeyboardFocusDetection(false);
            // Apply blur only on iOS and macOS and avoid blurring on Windows and Android to prevent losing the focus indicator
            if (isIOS() && isMacOS()) {
                actionAreaRef.current?.blur();
            }
            onMouseDown?.(event);
        },
        [disabled, onMouseDown]
    );

    const handleFocus = useCallback(
        (event: FocusEvent<T>) => {
            if (disabled) {
                return;
            }

            // Assume focus is keyboard-driven if no prior mouse interaction
            if (isKeyboardFocusDetection || !isHovered) {
                setIsFocusVisible(true);
            }
            onFocus?.(event);
        },
        [disabled, isHovered, isKeyboardFocusDetection, onFocus]
    );

    const handleBlur = useCallback(
        (event: FocusEvent<T>) => {
            if (disabled) {
                return;
            }
            setIsFocusVisible(false);
            onBlur?.(event);
        },
        [disabled, onBlur]
    );

    const handleKeyDown = useCallback(
        (event: KeyboardEvent<T>) => {
            if (disabled) {
                return;
            }

            if (event.code === 'Space' || event.code === 'Enter') {
                event.preventDefault();
                onClick?.(event as unknown as MouseEvent<T>);
            }

            if (event.code === 'Tab') {
                setIsKeyboardFocusDetection(true);
                actionAreaRef.current?.focus();
            }

            onKeyDown?.(event);
        },
        [disabled, onClick, onKeyDown]
    );

    const handleClick = useCallback(
        (event: MouseEvent<T>) => {
            if (disabled) {
                return;
            }
            event.stopPropagation();
            onClick?.(event);
        },
        [disabled, onClick]
    );

    const associatedId = useMemo(() => id ?? uniqueId('action-area-'), [id]);

    if (!enabled) {
        return {
            containerProps: {},
            actionAreaProps: {}
        };
    }

    return {
        containerProps: {
            'data-hover': isHovered || null,
            'data-focus-visible': isFocusVisible || null,
            'data-interactive': !disabled || null,
            'data-disabled': disabled || null,
            id: associatedId
        },
        actionAreaProps: {
            ref: actionAreaRef,
            onMouseEnter: handleMouseEnter,
            onMouseLeave: handleMouseLeave,
            onMouseDown: handleMouseDown,
            onFocus: handleFocus,
            onBlur: handleBlur,
            onKeyDown: handleKeyDown,
            onClick: handleClick,
            'aria-labelledby': `${associatedId}-content`,
            disabled
        }
    };
};
